import {
  CheckCircleOutlined,
  LockOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Button, Col, Input, Row, Typography, Upload } from "antd";
import type { RcFile } from "antd/es/upload";
import { CvConfigDto, uploadFiel } from "api/generated";
import { handleAxiosError } from "common/helpers/generalUtils";
import { useClient } from "hooks/useClient";
import { useState } from "react";

// Los configKeys que pertenecen a la FIEL y deben enviarse juntos
const FIEL_KEYS = new Set([
  "CERTIFICATE_FIEL_SAT",
  "PRIVATEKEY_FIEL_SAT",
  "PASSWORD_FIEL_SAT",
]);

export function isFielConfig(config: CvConfigDto): boolean {
  return config.configKey != null && FIEL_KEYS.has(config.configKey);
}

interface FielState {
  certificate?: File;
  privateKey?: File;
  passwordKey: string;
}

interface FielRowProps {
  config: CvConfigDto;
  fielState: FielState;
  onCertificateChange: (file: File) => void;
  onPrivateKeyChange: (file: File) => void;
  onPasswordChange: (value: string) => void;
}

// ─── Filas individuales por tipo ──────────────────────────────────────────────

function FielConfigRow({
  config,
  fielState,
  onCertificateChange,
  onPrivateKeyChange,
  onPasswordChange,
}: FielRowProps) {
  function renderFielInput() {
    switch (config.configKey) {
      case "CERTIFICATE_FIEL_SAT":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Upload
              accept=".cer" //FIXME?: Deberiamos soportar esto -> .cert,.crs
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file: RcFile) => {
                onCertificateChange(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>
                {fielState.certificate
                  ? fielState.certificate.name
                  : "Seleccionar certificado (.cer)"}
              </Button>
            </Upload>
            <Typography.Text
              type={config.value === "TRUE" ? "success" : "danger"}
            >
              {config.value === "TRUE" ? (
                <>
                  <CheckCircleOutlined style={{ marginRight: 4 }} />
                  Actualmente configurado
                </>
              ) : (
                "No configurado"
              )}
            </Typography.Text>
          </div>
        );
      case "PRIVATEKEY_FIEL_SAT":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Upload
              accept=".key"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file: RcFile) => {
                onPrivateKeyChange(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>
                {fielState.privateKey
                  ? fielState.privateKey.name
                  : "Seleccionar clave privada (.key)"}
              </Button>
            </Upload>
            <Typography.Text
              type={config.value === "TRUE" ? "success" : "danger"}
            >
              {config.value === "TRUE" ? (
                <>
                  <CheckCircleOutlined style={{ marginRight: 4 }} />
                  Actualmente configurada
                </>
              ) : (
                "No configurada"
              )}
            </Typography.Text>
          </div>
        );
      case "PASSWORD_FIEL_SAT":
        return (
          <Input.Password
            value={fielState.passwordKey}
            onChange={(e) => onPasswordChange(e.target.value)}
            placeholder="Contraseña de la firma electrónica"
            autoComplete="new-password"
          />
        );
      default:
        return null;
    }
  }

  return (
    <Row
      gutter={[16, 8]}
      style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}
    >
      <Col xs={24} sm={9}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Typography.Text strong>{config.description}</Typography.Text>
            {config.isProtected && (
              <Typography.Text type="secondary">
                <LockOutlined style={{ color: "#faad14" }} />
                Protegido
              </Typography.Text>
            )}
          </div>
          {config.requiresRestart && (
            <Typography.Text type="secondary">
              <ReloadOutlined style={{ color: "#f5222d" }} />
              Requiere reinicio
            </Typography.Text>
          )}
        </div>
      </Col>
      <Col xs={24} sm={15}>
        {renderFielInput()}
      </Col>
    </Row>
  );
}

interface FielSectionFormProps {
  configs: CvConfigDto[]; // los 3 configs FIEL ordenados
  onSuccess: () => void; // llamar para refrescar el árbol tras guardar
}

export function FielSectionForm({ configs, onSuccess }: FielSectionFormProps) {
  const { client } = useClient();
  const [saving, setSaving] = useState(false);
  const [fielState, setFielState] = useState<FielState>({ passwordKey: "" });

  const hasChanges =
    fielState.certificate != null ||
    fielState.privateKey != null ||
    fielState.passwordKey !== "";

  // Para enviar, los 3 campos son obligatorios
  const canSave =
    fielState.certificate != null &&
    fielState.privateKey != null &&
    fielState.passwordKey !== "";

  function handleSave() {
    if (
      !canSave ||
      fielState.certificate == null ||
      fielState.privateKey == null
    ) {
      return;
    }
    setSaving(true);

    const formData = new FormData();
    formData.append(
      "certificate",
      new Blob([fielState.certificate], { type: "application/octet-stream" }),
      fielState.certificate.name,
    );
    formData.append(
      "privateKey",
      new Blob([fielState.privateKey], { type: "application/octet-stream" }),
      fielState.privateKey.name,
    );

    uploadFiel({
      client,
      body: formData as never,
      query: { passwordKey: fielState.passwordKey },
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then(() => {
        onSuccess();
        setFielState({ passwordKey: "" });
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible actualizar la Firma Electrónica"),
      )
      .finally(() => setSaving(false));
  }

  function handleDiscard() {
    setFielState({ passwordKey: "" });
  }

  return (
    <div>
      {configs.map((cfg) => (
        <FielConfigRow
          key={cfg.id}
          config={cfg}
          fielState={fielState}
          onCertificateChange={(file) =>
            setFielState((prev) => ({ ...prev, certificate: file }))
          }
          onPrivateKeyChange={(file) =>
            setFielState((prev) => ({ ...prev, privateKey: file }))
          }
          onPasswordChange={(value) =>
            setFielState((prev) => ({ ...prev, passwordKey: value }))
          }
        />
      ))}

      {/*  Instrucciónes — solo visible cuando hay algo pendiente */}
      {hasChanges && (
        <Row
          justify="space-between"
          align="middle"
          style={{
            padding: "12px 16px",
            background: "#fffbe6",
            borderTop: "1px solid #ffe58f",
          }}
        >
          <Col>
            <Typography.Text type="secondary">
              {canSave
                ? "Los 3 campos están listos. Puedes guardar la firma electrónica."
                : "Para guardar la FIEL debes proporcionar el certificado, la clave privada y la contraseña."}
            </Typography.Text>
          </Col>
          <Col style={{ display: "flex", gap: 8 }}>
            <Button size="small" onClick={handleDiscard} disabled={saving}>
              Descartar
            </Button>
            <Button
              type="primary"
              size="small"
              loading={saving}
              disabled={!canSave}
              onClick={handleSave}
            >
              Guardar FIEL
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
}
